import {
    MAX_PAGE_SIZE_FOR_COMPARISON,
    STATISTICS,
} from '@client/app/shared-modules/statistics/statistics.constant';
import type {
    SiteAnalyticsDataRequest,
    SiteAnalyticsDataResult,
    SiteAnalyticsDataSourceKey,
    SiteAnalyticsScaffoldConfig,
    SiteAnalyticsTableRow,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-scaffold.type';
import type { SiteAnalyticsPageDefinitionDeps } from '@client/app/feature-modules/site-analytics/types/site-analytics-page-definition.type';
import type { CreateExport } from '@client/app/shared-modules/create-export/types/create-export.type';

type EventsChildField = 'event_actions' | 'event_names';

type EventsChildRow = Record<string, string | number>;

type EventsParentRow = {
    label?: string | number;
    event_count?: string | number;
    event_actions?: EventsChildRow[];
    event_names?: EventsChildRow[];
};

type EventsLoader = (
    domainId: number,
    params: Record<string, string | number>,
) => Promise<EventsParentRow[]>;

interface EventsConfigOptions {
    tableKey: string;
    dataSource: SiteAnalyticsDataSourceKey;
    exportCategory: CreateExport['payload']['category'];
    tableCaptionKey: string;
    primaryColumnHeaderKey: string;
    secondaryColumnHeaderKey: string;
    primarySortField: string;
}

export const EVENTS_HIERARCHICAL_ROWS_CONFIG = {
    rowTypeField: '__rowType',
    rowTypeParentValue: 'parent',
    rowTypeChildValue: 'child',
    parentRowIdField: '__parentRowId',
    toggleColumnId: 'toggle',
};

export function buildEventsScaffoldConfig (options: EventsConfigOptions): SiteAnalyticsScaffoldConfig {
    return {
        pageHeaderKey: 'siteAnalytics.events.header',
        pageSubheaderKey: 'siteAnalytics.events.subheader',
        tableKey: options.tableKey,
        dataSource: options.dataSource,
        exportCategory: options.exportCategory,
        searchPlaceholderKey: 'siteAnalytics.events.search.placeholder',
        emptyMessageKey: 'siteAnalytics.events.table.emptyMessage',
        emptySearchMessageKey: 'siteAnalytics.events.table.emptySearchMessage',
        tableCaptionKey: options.tableCaptionKey,
        exportButtonLabelKey: 'siteAnalytics.scaffold.export.buttonLabel',
        dateRangeLabelKey: 'siteAnalytics.scaffold.dateRange.label',
        compareToggleLabelKey: 'siteAnalytics.scaffold.compare.toggleLabel',
        compareDateRangeLabelKey: 'siteAnalytics.scaffold.compare.dateRangeLabel',
        defaultSortBy: options.primarySortField,
        defaultSortDir: 'desc',
        comparisonMaxPageSize: MAX_PAGE_SIZE_FOR_COMPARISON,
        comparisonEnabled: false,
        showChart: false,
        chart: {
            titleKey: 'siteAnalytics.events.chart.title',
            primarySeriesLabelKey: 'siteAnalytics.scaffold.chart.series.currentVisits',
            comparisonSeriesLabelKey: 'siteAnalytics.scaffold.chart.series.comparisonVisits',
        },
        hierarchicalRows: EVENTS_HIERARCHICAL_ROWS_CONFIG,
        columns: [
            {
                id: 'primaryLabel',
                headerKey: options.primaryColumnHeaderKey,
                displayType: 'text',
                sortable: true,
                sortField: options.primarySortField,
                width: 'xl',
            },
            {
                id: 'secondaryLabel',
                headerKey: options.secondaryColumnHeaderKey,
                displayType: 'text',
                width: 'xl',
            },
            {
                id: 'eventCount',
                headerKey: 'siteAnalytics.events.table.column.events',
                displayType: 'number',
                width: 'md',
            },
            {
                id: 'toggle',
                headerKey: '',
                displayType: 'text',
                width: 'xs',
            },
        ],
    };
}

export async function loadEventsTabData (
    request: SiteAnalyticsDataRequest,
    deps: SiteAnalyticsPageDefinitionDeps,
    loader: EventsLoader,
    childField: EventsChildField,
): Promise<SiteAnalyticsDataResult> {
    const params = buildEventsParams(request);
    const eventRows = await loader(request.domainId, params);
    const rows = normalizeRows(eventRows, childField);
    const parentRows = rows.filter((row) => isParentRow(row));

    return {
        rows,
        chart: {
            labels: parentRows.map((row) => String(row.primaryLabel || '')),
            primaryValues: parentRows.map((row) => toInteger(row.eventCount)),
        },
        summary: null,
        total: getTotal(eventRows, parentRows.length),
        perPage: getPerPage(eventRows, request.perPage),
        page: getCurrentPage(eventRows, request.page),
    };
}

function buildEventsParams (request: SiteAnalyticsDataRequest): Record<string, string | number> {
    const params: Record<string, string | number> = {
        page: request.page,
        limit: request.perPage,
        from: request.dateRange.startDate.format(STATISTICS.DATE_FORMAT),
        to: request.dateRange.endDate.format(STATISTICS.DATE_FORMAT),
        search: request.search,
    };

    if (request.sortBy) {
        params.sort_by = request.sortBy;
    }

    if (request.sortDir) {
        params.sort_dir = request.sortDir;
    }

    return params;
}

function normalizeRows (
    rows: EventsParentRow[],
    childField: EventsChildField,
): SiteAnalyticsTableRow[] {
    const normalizedRows: SiteAnalyticsTableRow[] = [];

    rows.forEach((row, rowIndex) => {
        const parentRowId = `parent-${rowIndex}`;

        normalizedRows.push({
            id: parentRowId,
            primaryLabel: String(row.label || '-'),
            secondaryLabel: '',
            eventCount: toInteger(row.event_count),
            toggle: '',
            __rowType: EVENTS_HIERARCHICAL_ROWS_CONFIG.rowTypeParentValue,
            __parentRowId: '',
        });

        const childRows = Array.isArray(row[childField])
            ? row[childField]
            : [];

        childRows.forEach((childRow, childIndex) => {
            normalizedRows.push({
                id: `${parentRowId}-child-${childIndex}`,
                primaryLabel: '',
                secondaryLabel: String(childRow.label || '-'),
                eventCount: toInteger(childRow.event_count),
                toggle: '',
                __rowType: EVENTS_HIERARCHICAL_ROWS_CONFIG.rowTypeChildValue,
                __parentRowId: parentRowId,
            });
        });
    });

    return normalizedRows;
}

function isParentRow (row: SiteAnalyticsTableRow): boolean {
    return row.__rowType === EVENTS_HIERARCHICAL_ROWS_CONFIG.rowTypeParentValue;
}

function toInteger (value: unknown): number {
    const parsed = Number.parseFloat(String(value ?? 0));
    return Number.isNaN(parsed) ? 0 : parsed;
}

function getTotal (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'total' in collection) {
        const total = Number((collection as { total?: unknown }).total);
        if (!Number.isNaN(total) && total > 0) {
            return total;
        }
    }

    return fallback;
}

function getPerPage (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'perPage' in collection) {
        const perPage = Number((collection as { perPage?: unknown }).perPage);
        if (!Number.isNaN(perPage) && perPage > 0) {
            return perPage;
        }
    }

    return fallback;
}

function getCurrentPage (collection: unknown, fallback: number): number {
    if (collection && typeof collection === 'object' && 'currentPage' in collection) {
        const currentPage = Number((collection as { currentPage?: unknown }).currentPage);
        if (!Number.isNaN(currentPage) && currentPage > 0) {
            return currentPage;
        }
    }

    return fallback;
}
